import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate, localizeCount } from '@trello/legacy-i18n';
import { asMoney } from '@trello/legacy-i18n/formatters';
import { Button } from '@trello/nachos/button';
import type { BillingIds, FreeTrialTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuote } from '../types';
import { PlanCardTotals } from './PlanCardTotals';

import styles from './PremiumPlanCard.less';

const format = forTemplate('billing_page_one');

export interface PremiumPlanCardProps {
  numOfMembers?: number;
  teamDisplayName: ReactNode;
  teamName: string;
  prices?: BillingQuote;
  isEligibleForTrial?: boolean;
  isMonthly: boolean;
  onShowFreeTrialModal: (planName: string) => void;
  sendAnalytics: (name: string) => void;
  isInPPCohort?: boolean;
}

export const PremiumPlanCard: FunctionComponent<PremiumPlanCardProps> = ({
  isEligibleForTrial,
  isMonthly,
  prices,
  numOfMembers = 1,
  teamDisplayName,
  onShowFreeTrialModal,
  sendAnalytics,
  isInPPCohort,
}) => {
  const pricePerUser = isMonthly
    ? prices?.monthly?.subTotalPerUser
    : prices?.annual?.subTotalPerUser;

  const productCode = isMonthly
    ? prices?.monthly?.productCode
    : prices?.annual?.productCode;

  const onClickFreeTrialButton = () => onShowFreeTrialModal('business class');
  const { onChoosePremium, variation } = useContext(PlanComparisonContext);

  const onClickUpgradeButton = useCallback(() => {
    sendAnalytics('business class');
    onChoosePremium?.(productCode!);
  }, [onChoosePremium, productCode, sendAnalytics]);

  const onKeepPremiumWithAnalytics = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'choosePremiumButton',
      source: 'trelloPlanPricingModal',
      attributes: {
        viaEndOfTrial: true,
      },
    });
    onChoosePremium?.(productCode!);
  }, [onChoosePremium, productCode]);

  let actionPrompt = null;
  switch (variation) {
    case 'reverse-trial-overlay':
      actionPrompt = (
        <Button onClick={onClickUpgradeButton} appearance="primary">
          {format('upgrade-now')}
        </Button>
      );
      break;
    case 'end-of-trial-friction-overlay':
      actionPrompt = (
        <Button onClick={onKeepPremiumWithAnalytics} appearance="primary">
          {'Keep Premium'}
        </Button>
      );
      break;
    case 'downgrade-plan-overlay':
      actionPrompt = (
        <section className={styles.currentPlan}>
          <div>{format('current-plan')}</div>
          <div>({format(isMonthly ? 'paid-monthly' : 'paid-yearly')})</div>
        </section>
      );
      break;
    case 'billing-page':
    default:
      actionPrompt = isEligibleForTrial ? (
        <>
          <Button
            onClick={onClickFreeTrialButton}
            appearance="primary"
            testId={getTestId<FreeTrialTestIds>('active-free-trial-banner')}
          >
            <WithEnterpriseManagedOverride>
              {localizeCount('try free', 14)}
            </WithEnterpriseManagedOverride>
          </Button>
          <p>
            {format('or', {
              purchaseNow: (
                <Button
                  key={`upgrade-${format('business-class-name')}`}
                  testId={getTestId<BillingIds>('upgrade-team-bc-button')}
                  appearance="link"
                  onClick={onClickUpgradeButton}
                >
                  {format('upgrade-now-lower-case')}
                </Button>
              ),
            })}
          </p>
        </>
      ) : (
        <Button
          onClick={onClickUpgradeButton}
          appearance="primary"
          testId={getTestId<BillingIds>('upgrade-team-button')}
        >
          {format('upgrade-now')}
        </Button>
      );
  }

  return (
    <section className={styles.planCard}>
      <article>
        <h3 className={styles.name}>{format('business-class-name')}</h3>
        <p className={styles.info}>
          {isInPPCohort
            ? format('premium-description-repackaging-gtm')
            : format('premium-description-repackaging-gtm')}
        </p>
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            {format('amount-usd', {
              amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
            })}
          </span>
          <span className={styles.frequency}>
            {format('per-user')}
            <br />
            {format(isMonthly ? 'per-month' : 'per-year')}
          </span>
        </div>
        <div className={styles.ctaContainer}>{actionPrompt}</div>
      </article>
      <PlanCardTotals
        isMonthly={isMonthly}
        numOfMembers={numOfMembers}
        teamName={teamDisplayName}
        prices={prices}
      />
    </section>
  );
};
